import { useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import { Group, Sync } from "@mui/icons-material";
import { DataTable } from "@/components/DataTable";
import usePagination from "@/hooks/pagination";
import { useNotification } from "@/context/NotificationContext";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { type User } from "@/api/user.service";
import { fetchAllUsers } from "@/api/allUsers";
import { getUsersByModule } from "@/api/module.service";
import { listModuleRoleGrants, grantModuleRole, revokeModuleRole } from "@/api/moduleRole.service";
import { MODULE_ID, ESCALA_ROLES, ESCALA_PAPEL_DESCRICAO } from "@/api/escala.service";
import { EscalaPermissoesModal } from "@/components/escala/EscalaPermissoesModal";

// Uma linha por papel (os quatro que a Escala conhece), não por usuário —
// quem está vinculado a cada um só aparece dentro do modal de vínculo.
interface PapelRow {
  role: (typeof ESCALA_ROLES)[number];
}

export function EscalaPermissoesTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();

  const [users, setUsers] = useState<User[]>([]);
  const [roleByUser, setRoleByUser] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [papelAberto, setPapelAberto] = useState<(typeof ESCALA_ROLES)[number] | null>(null);

  const loadData = async () => {
    try {
      const [allUsers, grants, doModulo] = await Promise.all([
        fetchAllUsers(),
        listModuleRoleGrants(clientId, MODULE_ID),
        getUsersByModule(MODULE_ID, clientId),
      ]);
      // Só quem tem o módulo pode receber papel; quem já tem papel fica na lista mesmo
      // sem o módulo, senão não haveria como tirá-lo.
      const comModulo = new Set(doModulo.map((m) => m.user_id));
      const comPapel = new Set(grants.map((g) => g.user_id));
      setUsers(allUsers.filter((u) => comModulo.has(u.id) || comPapel.has(u.id)));
      setRoleByUser(new Map(grants.map((g) => [g.user_id, g.role])));
    } catch {
      notify("Erro ao carregar permissões da Escala", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const rows = useMemo<PapelRow[]>(() => ESCALA_ROLES.map((role) => ({ role })), []);

  useEffect(() => {
    setTotal(rows.length);
  }, [rows, setTotal]);

  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const idsDoPapel = (role: string) =>
    Array.from(roleByUser.entries())
      .filter(([, r]) => r === role)
      .map(([userId]) => userId);

  const vinculadosDoPapelAberto = papelAberto ? idsDoPapel(papelAberto) : [];

  // Grant pra quem entrou, revoke pra quem saiu — trocar de papel (entrar
  // aqui já tendo outro) é só o grant do novo, o servidor revoga o anterior
  // sozinho (services.moduleRoleService.Grant, singleRoleModules["escala"]).
  // Promise.allSettled, não Promise.all: uma falha isolada não pode apagar o
  // resultado de quem já vinculou/desvinculou com sucesso. Rejeita no final
  // se sobrou falha, pra o modal não fechar sozinho e o admin poder repetir
  // só o que não pegou (o diff da próxima tentativa já parte do que já foi
  // aplicado aqui).
  const handleSave = async (role: string, ids: string[]) => {
    const antes = new Set(vinculadosDoPapelAberto);
    const depois = new Set(ids);
    const adicionados = ids.filter((id) => !antes.has(id));
    const removidos = vinculadosDoPapelAberto.filter((id) => !depois.has(id));
    if (adicionados.length === 0 && removidos.length === 0) return;

    const operacoes = [
      ...adicionados.map((user_id) => ({ user_id, novoRole: role as string | null, promessa: grantModuleRole({ user_id, client_id: clientId, module_id: MODULE_ID, role }) })),
      ...removidos.map((user_id) => ({ user_id, novoRole: null as string | null, promessa: revokeModuleRole({ user_id, client_id: clientId, module_id: MODULE_ID, role }) })),
    ];
    const resultados = await Promise.allSettled(operacoes.map((o) => o.promessa));
    const sucesso = operacoes.filter((_, i) => resultados[i]!.status === "fulfilled");
    const falhas = operacoes.length - sucesso.length;

    if (sucesso.length > 0) {
      setRoleByUser((prev) => {
        const next = new Map(prev);
        for (const op of sucesso) {
          if (op.novoRole) next.set(op.user_id, op.novoRole);
          else next.delete(op.user_id);
        }
        return next;
      });
      for (const op of sucesso) {
        log(op.novoRole ? LogEventType.Updated : LogEventType.Deleted, { user_id: op.user_id, role }, "module_role", op.user_id, "info");
      }
    }

    if (falhas > 0) {
      notify(`${falhas} de ${operacoes.length} alterações não foram salvas`, "error");
      throw new Error("Algumas permissões não foram salvas");
    }
    notify("Permissões salvas!", "success");
  };

  if (isLoading && users.length === 0) return <Sync />;

  return (
    <>
      <DataTable
        data={paged}
        columns={[
          { key: "role", label: "Papel" },
          { key: "descricao", label: "O que faz", render: (row) => ESCALA_PAPEL_DESCRICAO[row.role].resumo },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(row) => (
          <IconButton aria-label={`Vincular pessoas — ${row.role}`} onClick={() => setPapelAberto(row.role)}>
            <Group />
          </IconButton>
        )}
      />

      <EscalaPermissoesModal
        role={papelAberto}
        users={users}
        vinculadosIds={vinculadosDoPapelAberto}
        roleByUser={roleByUser}
        onClose={() => setPapelAberto(null)}
        onSave={(ids) => handleSave(papelAberto!, ids)}
      />
    </>
  );
}
